import { TFunction } from 'i18next';
import { TermsAndConditionsStatus } from '../types/TermsAndConditionsStatus';
import { CognitoUser } from '../types/User';
import { downloadObjectAsJson } from '../utils/download.utils';
import { i18nKeys } from '../utils/i18n.utils';
import { ApiClient } from './ApiClient';

export interface ListUserResponse {
  userResponses?: CognitoUser[];
  nextToken: string;
}

/**
 * Class for constructing a client to make account API calls
 */
export class AccountAPI {
  constructor(private apiClient: ApiClient, private t: TFunction) {
    // do nothing
  }

  /** Get List of users */
  public listUsers(params: { [key: string]: string | boolean | string[] }) {
    return this.apiClient.get({
      path: `/admin/users`,
      params,
      failMessage: this.t(i18nKeys.errors.requestFailed.user.listUsers),
    }) as Promise<ListUserResponse>;
  }

  public async deleteUser(username: string): Promise<void> {
    return (await this.apiClient.delete({
      path: `/admin/users/${username}`,
      successMessage: this.t(i18nKeys.success.requestSucceeded.user.deleteUser),
      failMessage: this.t(i18nKeys.errors.requestFailed.user.deleteUser),
    })) as Promise<void>;
  }

  public async removeUserFromGroup(vibeId: string, group: string): Promise<void> {
    await this.apiClient.post({
      path: `/admin/users/groups/remove`,
      body: {
        vibeId,
        group,
      },
      successMessage: this.t(i18nKeys.success.requestSucceeded.user.removeUserFromGroup),
      failMessage: this.t(i18nKeys.errors.requestFailed.user.removeUserFromGroup),
    });
  }

  public async addUserToGroup(vibeId: string, group: string): Promise<void> {
    await this.apiClient.post({
      path: `/admin/users/groups/add`,
      body: {
        vibeId,
        group,
      },
      successMessage: this.t(i18nKeys.success.requestSucceeded.user.addUserToGroup),
      failMessage: this.t(i18nKeys.errors.requestFailed.user.addUserToGroup),
    });
  }

  async getConsoleUserTermStatus(): Promise<TermsAndConditionsStatus> {
    return (await this.apiClient.get({
      path: '/admin/terms/status',
      failMessage: this.t(i18nKeys.errors.requestFailed.getUserTermStatus),
    })) as Promise<TermsAndConditionsStatus>;
  }

  async acceptTermsAndConditions(): Promise<void> {
    (await this.apiClient.post({
      path: '/admin/terms/accept',
      failMessage: this.t(i18nKeys.errors.requestFailed.acceptTermsAndConditions),
    })) as Promise<void>;
  }

  async getLastFinishedTutorialDate(silent = true): Promise<{ lastFinishedTutorialOn: string }> {
    return (await this.apiClient.get({
      path: '/admin/challenge/tutorial',
      failMessage: this.t(i18nKeys.errors.requestFailed.user.getLastFinishedTutorialDate),
      silent,
    })) as Promise<{ lastFinishedTutorialOn: string }>;
  }

  async updateLastFinishedTutorialDate(silent = true): Promise<void> {
    return (await this.apiClient.post({
      path: '/admin/challenge/tutorial',
      failMessage: this.t(i18nKeys.errors.requestFailed.user.updateLastFinishedTutorialDate),
      silent,
    })) as Promise<void>;
  }

  async makeDSARRequest(email: string, silent = false): Promise<void> {
    const response = (await this.apiClient.get({
      path: '/admin/gdpr/dsar',
      params: {
        email,
      },
      successMessage: this.t(i18nKeys.success.requestSucceeded.account.makeDSARRequest),
      failMessage: this.t(i18nKeys.errors.requestFailed.user.makeDSARRequest),
      silent,
    })) as unknown;

    if (response) {
      downloadObjectAsJson(response, `aws-jam-personal-data-${Date.now()}`);
    }
  }

  async makeRTFRequest(email: string, silent = false): Promise<void> {
    return (await this.apiClient.delete({
      path: '/admin/gdpr/rtf',
      params: {
        email,
      },
      successMessage: this.t(i18nKeys.success.requestSucceeded.account.makeRTFRequest),
      failMessage: this.t(i18nKeys.errors.requestFailed.user.makeRTFRequest),
      silent,
    })) as Promise<void>;
  }
}
